import {
  Box,
  Flex,
  IconButton,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Composition,
  compositionApi,
  CompositionMaterial,
  UpdateCompositionMaterialRequest,
} from '@texas/api/endpoints/compositionApi';
import { CompositionImage } from './CompositionImage';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { CompositionName } from './CompositionName';
import { useTranslation } from 'react-i18next';
import { DimensionGroupContainer } from '../../../../shared/composition/dimension/DimensionGroupContainer';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { RenameCompositionModal } from '@texas/components/shared/composition/RenameCompositionModal';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { AddMaterial } from '@texas/components/shared/composition/material/AddMaterial';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { CompositionMaterialView } from '@texas/components/shared/composition/material/CompositionMaterialView';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { UpdateCompositionMaterial } from '@texas/components/shared/composition/material/UpdateCompositionMaterial';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ServerError } from '@texas/types';
import { TFunction } from 'i18next';
import { DimensionValuesContainer } from '@texas/components/shared/composition/dimension/DimensionValuesContainer';
import { CompositionHistoryModal } from '../../history/CompositionHistoryModal';

interface Props {
  groupHasAnyBasedOn: boolean;
  disableArchive: boolean;
  isMatrix: boolean;
  isReady: boolean;
  composition: Composition;
  groupId: number;
  vdcTemplate: boolean;
  isVdc: boolean;
  index: number;
  onMaterialChanged: () => void;
}

export function CompositionComponent(props: Props) {
  const { t } = useTranslation();

  const {
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useDisclosure();
  const {
    isOpen: isRenameOpen,
    onClose: onRenameClose,
    onOpen: onRenameOpen,
  } = useDisclosure();
  const {
    isOpen: editIsOpen,
    value,
    onClose: editOnClose,
    onOpen: editOnOpen,
  } = useValueDisclosure<CompositionMaterial>();
  const {
    isOpen: isHistoryOpen,
    onClose: onHistoryClose,
    onOpen: onHistoryOpen,
    value: historyValue,
  } = useValueDisclosure<{
    id: number;
    name: string;
  }>();

  const toast = useToast();

  const { request: updateRequest } = useApiRequest(
    compositionApi.updateCompositionMaterial,
  );
  const onSubmit = async (data: UpdateCompositionMaterialRequest) =>
    await request(
      updateRequest,
      [value!.id, data],
      (_) => {
        compositionEvents.onMaterialChanged.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.updateFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  return (
    <Box w="auto" maxW="400px" animation={fadeInScaleAnimation()} role="group">
      <Flex gap={1} pb={2} align="flex-end" justify="space-between" px={1}>
        <CompositionName
          note={props.composition.note}
          index={props.index + 1}
        />
        <Flex
          align="flex-end"
          transition="opacity 200ms ease, transform 200ms ease"
          opacity={0}
          marginRight="-22px"
          _groupHover={{
            opacity: 1,
            paddingLeft: 1,
            transform: 'translateX(-22px)',
          }}
        >
          {!props.isVdc && (
            <Tooltip label={t('general.rename')}>
              <IconButton
                onClick={onRenameOpen}
                aria-label={t('general.rename')}
                variant="no-bg-dark"
                icon={<Icons.NoteEdit boxSize={defaultIconSize} />}
                alignItems="flex-end"
              />
            </Tooltip>
          )}

          <Tooltip label={t('history.viewHistory')}>
            <IconButton
              onClick={() =>
                onHistoryOpen({
                  id: props.composition.id,
                  name: props.composition.note
                    ? props.composition.note
                    : t('composition.compositionNr', {
                        nr: props.index + 1,
                      }),
                })
              }
              variant="no-bg-dark"
              icon={<Icons.Clock boxSize={defaultIconSize} />}
              aria-label={t('history.viewHistory')}
              alignItems="flex-end"
            />
          </Tooltip>
          <Tooltip
            label={
              props.groupHasAnyBasedOn
                ? t('compositionGroup.option.hasVersions')
                : t('general.archive')
            }
          >
            <IconButton
              color="red.200"
              _light={{ color: 'red.400' }}
              isDisabled={
                props.groupHasAnyBasedOn || props.disableArchive || props.isVdc
              }
              onClick={onArchiveOpen}
              icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
              variant="no-bg-dark"
              aria-label={t('general.archive')}
              alignItems="flex-end"
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Box
        background="texas.bg.900"
        _light={{
          bg: 'white',
          boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.35);',
        }}
        borderRadius="md"
        boxShadow="0px 0px 10px 0px rgba(0,0,0,0.75);"
      >
        <Box
          boxSize="400px"
          padding="4"
          sx={{
            '@container options (max-width: 400px)': {
              display: 'none',
            },
          }}
        >
          <CompositionImage
            fileId={props.composition.fileId}
            compositionId={props.composition.id}
            folderId={props.composition.folderId}
            compositionName={
              props.composition.note
                ? props.composition.note
                : t('composition.compositionNr', {
                    nr: props.index + 1,
                  })
            }
          />
        </Box>
        <Flex p={2} flexDir="column" gap={4}>
          <Tooltip
            label={
              props.groupHasAnyBasedOn
                ? t('compositionGroup.option.hasVersions')
                : undefined
            }
          >
            <Box>
              <WarningComponent
                isMandatory={true}
                warning={missingDimensionGroup(props.composition, t)}
                w="auto"
              >
                <DimensionGroupContainer
                  mode={
                    props.groupHasAnyBasedOn || props.isVdc
                      ? 'read-only'
                      : props.isMatrix
                      ? 'only-group'
                      : undefined
                  }
                  composition={props.composition}
                  onGroupChange={() =>
                    compositionEvents.onDimensionGroupSet.dispatch(
                      props.composition.id,
                    )
                  }
                  isReady={props.isReady}
                  isMatrix={props.isMatrix}
                >
                  <DimensionValuesContainer
                    composition={props.composition}
                    groupId={props.groupId}
                    isReady={props.isReady}
                  />
                </DimensionGroupContainer>
              </WarningComponent>
            </Box>
          </Tooltip>

          {props.composition.materials.map((x) => {
            return (
              <CompositionMaterialView
                key={x.id}
                onClick={() => editOnOpen(x)}
                onRemoved={() => compositionEvents.onMaterialRemoved.dispatch()}
                material={x}
                settings={{
                  colors: props.isMatrix ? 'hidden' : undefined,
                  isVdc: props.isVdc,
                }}
              />
            );
          })}

          {!props.isVdc && (
            <WarningComponent
              isMandatory={true}
              warning={anyMaterialMissing(props.composition, t)}
              w="auto"
            >
              <AddMaterial
                formType={props.isMatrix ? 'controller' : undefined}
                compositionId={props.composition.id}
                productGroupId={props.composition.articleProductGroupId}
                onCreated={() => compositionEvents.onMaterialAdded.dispatch()}
                isReady={props.isReady}
                groupHasAnyBasedOn={props.groupHasAnyBasedOn}
                vdcTemplate={props.vdcTemplate}
              />
            </WarningComponent>
          )}
        </Flex>
      </Box>

      {value && (
        <UpdateCompositionMaterial
          formType={
            props.isMatrix ? 'controller' : props.isVdc ? 'vdc' : undefined
          }
          onSubmit={onSubmit}
          compositionMaterialId={value.id}
          productGroupId={props.composition.articleProductGroupId}
          isOpen={editIsOpen}
          onClose={editOnClose}
          isReady={props.isReady}
          groupHasAnyBasedOn={props.groupHasAnyBasedOn}
          vdcTemplate={props.vdcTemplate}
        />
      )}
      <VerifyDialogWithRequest
        headerTitle={t('composition.archiveComposition')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        isOpen={isArchiveOpen}
        onClose={onArchiveClose}
        onPerformed={() => compositionEvents.onArchived.dispatch()}
        primaryRequest={compositionApi.archiveComposition}
        args={[props.composition.id]}
        onSuccessTitle={t('composition.archived')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <RenameCompositionModal
        composition={props.composition}
        onUpdated={() => compositionEvents.onRename.dispatch()}
        isOpen={isRenameOpen}
        onClose={onRenameClose}
      />
      {historyValue && (
        <CompositionHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          compositionId={historyValue.id}
          name={historyValue.name}
        />
      )}
    </Box>
  );
}

function missingDimensionGroup(composition: Composition, t: TFunction) {
  return composition.dimensionGroup === null
    ? t('composition.missingDimensionGroup')
    : undefined;
}

function anyMaterialMissing(composition: Composition, t: TFunction) {
  return composition.materials.length === 0
    ? t('composition.materialMissing')
    : undefined;
}
